import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ApiCompetitionSlim, PlayerRead, GameLogLeaderboardEntry } from '@/api/schemas';
import { fetchAllCompetitions } from '@/api/dfs';
import { fetchGameLogLeaderboard } from '@/api/stats';
import { useAuth } from '@/contexts/AuthContext';
import { getAbbreviation } from '@/utils/teams';
import {
  SORT_OPTIONS,
  STAT_COLUMNS,
  SORTABLE_KEYS,
  COLUMN_TO_SORT_KEY,
  formatShortDate,
} from '@/utils/gameLogColumns';
import PlayerProfileModal from '@/components/common/PlayerProfileModal';
import SEO from '@/components/common/SEO';
import Spinner from '@/components/common/Spinner';
import { userFacingError } from '@/utils/userFacingError';

const ROWS_PER_PAGE = 50;

type PositionFilter = 'ALL' | 'FW' | 'MF' | 'DF' | 'GK';

const GameLogLeaderboardPage: React.FC = () => {
  const { token } = useAuth();

  const [competitions, setCompetitions] = useState<ApiCompetitionSlim[]>([]);
  const [isLoadingCompetitions, setIsLoadingCompetitions] = useState(true);
  const [competitionsError, setCompetitionsError] = useState<string | null>(null);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState<string>('');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ sortBy: string; sortOrder: 'asc' | 'desc' }>({
    sortBy: 'fantasy_points',
    sortOrder: 'desc',
  });
  const [positionFilter, setPositionFilter] = useState<PositionFilter>('ALL');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [selectedPlayerForModal, setSelectedPlayerForModal] = useState<PlayerRead | null>(null);
  const [modalCompetitionContext, setModalCompetitionContext] = useState<{
    id: number | null;
    name: string | null;
  } | null>(null);

  const numericCompetitionId = useMemo(
    () => (selectedCompetitionId ? parseInt(selectedCompetitionId, 10) : null),
    [selectedCompetitionId]
  );

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const loadCompetitions = async () => {
      setIsLoadingCompetitions(true);
      try {
        const comps = await fetchAllCompetitions(true);
        setCompetitions(comps);
        if (comps.length > 0) {
          // Sticky league preference: respect prior selection, default to EPL,
          // fall back to first available. Mirrors PublicPlayerStatsPage.
          const stored = typeof window !== 'undefined'
            ? window.localStorage.getItem('gameLogLeaderboard.competitionId')
            : null;
          const storedComp = stored ? comps.find(c => String(c.id) === stored) : undefined;
          const premier = comps.find(c => c.short_name === 'EPL' || c.name === 'Premier League');
          setSelectedCompetitionId(String((storedComp ?? premier ?? comps[0]).id));
        }
      } catch (err: any) {
        setCompetitionsError(userFacingError(err, 'Failed to load leagues.'));
      } finally {
        setIsLoadingCompetitions(false);
      }
    };
    loadCompetitions();
  }, []);

  const {
    data: queryData,
    error: queryError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useInfiniteQuery({
    queryKey: [
      'gameLogLeaderboard',
      numericCompetitionId,
      sortConfig.sortBy,
      sortConfig.sortOrder,
      positionFilter,
      debouncedSearchTerm,
      dateFrom,
      dateTo,
    ],
    queryFn: ({ pageParam = 0 }) => {
      if (!numericCompetitionId) return Promise.resolve([]);
      return fetchGameLogLeaderboard(
        numericCompetitionId,
        {
          sort_by: sortConfig.sortBy,
          order: sortConfig.sortOrder,
          position: positionFilter === 'ALL' ? undefined : positionFilter,
          search: debouncedSearchTerm || undefined,
          skip: pageParam * ROWS_PER_PAGE,
          limit: ROWS_PER_PAGE,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
        token
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (debouncedSearchTerm) return undefined;
      return lastPage.length === ROWS_PER_PAGE ? allPages.length : undefined;
    },
    enabled: !!numericCompetitionId,
    refetchOnWindowFocus: false,
  });

  const allRows: GameLogLeaderboardEntry[] = useMemo(
    () => queryData?.pages.flatMap(page => page) ?? [],
    [queryData]
  );

  const handlePlayerClick = useCallback(
    (entry: GameLogLeaderboardEntry) => {
      const currentComp = competitions.find(c => String(c.id) === selectedCompetitionId);
      if (currentComp) {
        const playerForModal: PlayerRead = {
          id: entry.player_id,
          name: entry.player_name,
          primary_position: entry.primary_position ?? null,
          current_team_name: entry.current_team_name ?? null,
        } as PlayerRead;
        setSelectedPlayerForModal(playerForModal);
        setModalCompetitionContext({ id: numericCompetitionId, name: currentComp.name });
        setIsProfileModalOpen(true);
      }
    },
    [competitions, selectedCompetitionId, numericCompetitionId]
  );

  const handleSortChange = (columnKey: string) => {
    const sortKey = COLUMN_TO_SORT_KEY[columnKey] || columnKey;
    if (sortConfig.sortBy === sortKey) {
      setSortConfig({ sortBy: sortKey, sortOrder: sortConfig.sortOrder === 'desc' ? 'asc' : 'desc' });
    } else {
      setSortConfig({ sortBy: sortKey, sortOrder: 'desc' });
    }
  };

  const pageError = queryError instanceof Error ? queryError : null;
  const isInitialLoading = isLoadingCompetitions || (isLoading && !queryData);

  return (
    <>
      <SEO
        title="Game Logs | Fantasy Futbol"
        description="Match-by-match fantasy performance logs for every Premier League and La Liga player. Sort by goals, shots, progressive passes, goal-creating actions, and fantasy points."
        path="/game-logs"
      />
      <PlayerProfileModal
        isOpen={isProfileModalOpen}
        rankCompetitionIds={numericCompetitionId ? [numericCompetitionId] : []}
        player={selectedPlayerForModal}
        onClose={() => {
          setIsProfileModalOpen(false);
          setSelectedPlayerForModal(null);
          setModalCompetitionContext(null);
        }}
        competitionId={modalCompetitionContext?.id ?? null}
        competitionName={modalCompetitionContext?.name ?? null}
        pinCompetition
        isMyPlayer={false}
        onDropPlayer={() => {}}
        onAddPlayer={() => {}}
        onTradePlayer={() => {}}
        isActionLoading={false}
      />

      <div className="min-h-screen bg-primary p-4 md:p-8 text-gray-200 font-body">
        <h1 className="text-3xl font-heading font-bold text-accent-light-blue mb-6 text-center">
          Game Logs
        </h1>

        <div className="max-w-7xl mx-auto">
          <div className="mb-6 max-w-md mx-auto">
            <label htmlFor="comp-select-gl" className="block text-sm font-medium text-gray-400 mb-1">
              Select League:
            </label>
            <select
              id="comp-select-gl"
              value={selectedCompetitionId}
              onChange={(e) => {
                setSelectedCompetitionId(e.target.value);
                setDateFrom('');
                setDateTo('');
                try { window.localStorage.setItem('gameLogLeaderboard.competitionId', e.target.value); } catch {}
              }}
              disabled={isLoadingCompetitions}
              className="w-full px-3 py-1.5 bg-primary border border-gray-600 rounded text-sm text-gray-200 focus:outline-none focus:border-accent-yellow"
            >
              {isLoadingCompetitions && <option>Loading...</option>}
              {competitionsError && <option disabled>{competitionsError}</option>}
              {competitions.map(comp => (
                <option key={comp.id} value={String(comp.id)}>{comp.name}</option>
              ))}
            </select>
          </div>

          <div className="max-w-7xl mx-auto px-2 sm:px-4">
            {/* Header bar */}
            <div className="bg-surface-card rounded-t-lg border border-gray-700 px-3 py-3 mb-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-xl font-heading font-bold text-accent-yellow">
                  Single-Game Performances
                </h2>
                <div className="flex items-center space-x-4 text-xs text-gray-400">
                  <span>Showing: {allRows.length}</span>
                  {debouncedSearchTerm && (
                    <span className="text-accent-yellow">Searching: "{debouncedSearchTerm}"</span>
                  )}
                  {isFetching && !isInitialLoading && (
                    <span className="text-blue-400">Updating...</span>
                  )}
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-primary-dark border-x border-gray-700 px-3 py-2">
              <div className="flex flex-col lg:flex-row gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search players..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1.5 bg-primary border border-gray-600 rounded text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={positionFilter}
                    onChange={(e) => setPositionFilter(e.target.value as PositionFilter)}
                    className="px-3 py-1.5 bg-primary border border-gray-600 rounded text-sm text-gray-200 focus:outline-none focus:border-accent-yellow"
                  >
                    <option value="ALL">All Positions</option>
                    <option value="FW">FW</option>
                    <option value="MF">MF</option>
                    <option value="DF">DF</option>
                    <option value="GK">GK</option>
                  </select>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="px-2 py-1.5 bg-primary border border-gray-600 rounded text-sm text-gray-200 focus:outline-none focus:border-accent-yellow"
                    title="From date"
                  />
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="px-2 py-1.5 bg-primary border border-gray-600 rounded text-sm text-gray-200 focus:outline-none focus:border-accent-yellow"
                    title="To date"
                  />
                  {/* Mobile sort dropdown */}
                  <select
                    value={sortConfig.sortBy}
                    onChange={(e) => handleSortChange(e.target.value)}
                    className="sm:hidden px-3 py-1.5 bg-primary border border-gray-600 rounded text-sm text-gray-200 focus:outline-none focus:border-accent-yellow"
                  >
                    <optgroup label="Performance">
                      {SORT_OPTIONS.filter(opt => opt.category === 'performance').map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Stats">
                      {SORT_OPTIONS.filter(opt => opt.category === 'stats').map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Other">
                      {SORT_OPTIONS.filter(opt => opt.category === 'info').map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  </select>
                  <button
                    onClick={() => setSortConfig(prev => ({ ...prev, sortOrder: prev.sortOrder === 'desc' ? 'asc' : 'desc' }))}
                    className="sm:hidden px-3 py-1.5 bg-primary border border-gray-600 rounded text-sm text-gray-200 focus:outline-none focus:border-accent-yellow"
                    title={sortConfig.sortOrder === 'desc' ? 'Switch to ascending' : 'Switch to descending'}
                  >
                    {sortConfig.sortOrder === 'desc' ? '▼' : '▲'}
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-primary-dark rounded-b-lg border-x border-b border-gray-700 overflow-hidden">
              {isInitialLoading ? (
                <div className="flex justify-center items-center py-20">
                  <Spinner size="lg" />
                  <span className="ml-3 text-gray-400">Loading game logs...</span>
                </div>
              ) : pageError ? (
                <div className="text-center p-10 text-secondary bg-red-900/30 rounded">
                  <p className="font-semibold text-lg">Could not load game logs:</p>
                  <p>{userFacingError(pageError, 'An unknown error occurred.')}</p>
                </div>
              ) : (
                <>
                  {isFetching && !isInitialLoading && debouncedSearchTerm && (
                    <div className="flex justify-center items-center py-10">
                      <Spinner size="md" />
                      <span className="ml-3 text-gray-400">Searching for "{debouncedSearchTerm}"...</span>
                    </div>
                  )}

                  {(!isFetching || !debouncedSearchTerm) && (
                    <div className="flex flex-col h-full">
                      <div className="flex-grow overflow-x-auto overflow-y-auto scrollbar-mobile-hide">
                        <table className="w-full text-left text-xs sm:text-sm">
                          <thead className="sticky top-0 bg-surface-card z-10">
                            <tr>
                              {STAT_COLUMNS.map(({ key, label, mobileHidden, align }) => {
                                const sortKey = COLUMN_TO_SORT_KEY[key] || key;
                                const isSortable = SORTABLE_KEYS.has(sortKey);
                                const isActive = sortConfig.sortBy === sortKey;
                                let thClasses = `p-2 font-semibold uppercase tracking-wider whitespace-nowrap text-${align ?? 'left'} select-none`;
                                thClasses += isActive ? ' text-accent-yellow' : ' text-gray-400';
                                if (isSortable) thClasses += ' cursor-pointer hover:text-gray-200';
                                if (mobileHidden) thClasses += ' hidden sm:table-cell';
                                if (key === 'player_name') thClasses += ' sticky top-0 left-0 bg-surface-card z-20 min-w-[140px]';
                                return (
                                  <th
                                    key={key}
                                    className={thClasses}
                                    onClick={isSortable ? () => handleSortChange(key) : undefined}
                                    title={isSortable ? `Sort by ${label}` : undefined}
                                  >
                                    {label}
                                    {isActive && (
                                      <span className="ml-1 text-badge">
                                        {sortConfig.sortOrder === 'asc' ? '▲' : '▼'}
                                      </span>
                                    )}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700">
                            {allRows.map((entry, idx) => (
                              <tr
                                key={`${entry.player_id}-${entry.match_id}-${idx}`}
                                className="cursor-pointer hover:bg-primary/50 transition-colors duration-150 ease-in-out"
                                onClick={() => handlePlayerClick(entry)}
                              >
                                {STAT_COLUMNS.map(({ key, mobileHidden, align }) => {
                                  let value: string = '-';
                                  let title: string | undefined = undefined;
                                  const cellAlignmentClass = `text-${align ?? 'left'}`;
                                  const cellHiddenClass = mobileHidden ? 'hidden sm:table-cell' : '';

                                  if (key === 'player_name') {
                                    value = entry.player_name;
                                    title = value;
                                  } else if (key === 'current_team_name') {
                                    value = getAbbreviation(entry.current_team_name) || entry.current_team_name || '-';
                                    title = entry.current_team_name ?? undefined;
                                  } else if (key === 'primary_position') {
                                    value = entry.primary_position ?? '-';
                                  } else if (key === 'match_date') {
                                    value = formatShortDate(entry.match_date);
                                    title = entry.match_date;
                                  } else if (key === 'opponent') {
                                    value = entry.opponent ?? '-';
                                  } else if (key === 'fantasy_points') {
                                    value = entry.fantasy_points.toFixed(1);
                                  } else {
                                    const numVal = entry[key as keyof GameLogLeaderboardEntry];
                                    if (typeof numVal === 'number') {
                                      value = String(numVal);
                                    }
                                  }

                                  let tdClasses = `p-2 truncate ${cellHiddenClass} ${cellAlignmentClass}`;
                                  if (key === 'player_name') {
                                    tdClasses += ' sticky left-0 z-10 bg-surface-card font-semibold hover:text-accent-yellow hover:underline hover:underline-offset-2';
                                  } else if (key === 'fantasy_points') {
                                    tdClasses += ' font-bold text-accent-yellow';
                                  }

                                  return (
                                    <td key={key} title={title} className={tdClasses}>
                                      {value}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {allRows.length === 0 && !isLoading && !isFetching && (
                    <div className="text-center py-20 text-gray-500">
                      <p className="text-lg mb-2">No game logs found</p>
                      <p className="text-sm">
                        {debouncedSearchTerm
                          ? `No results for "${debouncedSearchTerm}". Try a different search term.`
                          : 'Try adjusting your filters'}
                      </p>
                    </div>
                  )}

                  {!debouncedSearchTerm && (
                    <div className="p-4 text-center border-t border-gray-700">
                      {isFetchingNextPage ? (
                        <div className="flex justify-center items-center py-2">
                          <Spinner size="sm" />
                          <span className="ml-2 text-gray-400 text-sm">Loading more...</span>
                        </div>
                      ) : hasNextPage && allRows.length > 0 ? (
                        <button
                          onClick={() => fetchNextPage()}
                          className="bg-accent-light-blue hover:bg-blue-500 text-primary font-semibold py-2 px-6 rounded-md transition-colors text-sm"
                        >
                          Load More
                        </button>
                      ) : allRows.length > 0 ? (
                        <p className="text-sm text-gray-500 italic">All game logs loaded</p>
                      ) : null}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GameLogLeaderboardPage;
